/**
 * The typed API client (CC-7). ONE fetch surface for the whole SPA; every failure
 * becomes a typed `ApiError` carrying `status` + machine `code` + a non-revealing
 * `message` (never a stack). The QueryClient 401 handler (query-client.ts) and the
 * auth store both narrow on `ApiError.status`.
 *
 * TWO same-origin bases, both served through the staging app shell (apps/web/shell-worker.mjs):
 *  - `/auth`   → worker-auth: the IDENTITY plane (sign-in, TOTP, the firm picker + select,
 *               invites). Its wire shapes are pinned by the shared contract fixtures in
 *               contracts/fixtures/worker-auth (worker-auth's own suite proves its handlers
 *               produce them; the SPA's tests consume the same files).
 *  - `/api/v1` → worker-api: the FIRM data plane, bound from the `argus_firm` session.
 * Requests use `credentials: 'include'` so the HttpOnly `argus_identity` / `argus_firm`
 * cookies ride along — the SPA never sees or stores a token (auth-and-authz.md §1.3). The SPA
 * never calls worker-api's legacy `/api/v1/auth/login` or `/api/v1/invite/*` routes.
 */
import type { Permission, Role } from '@argus/permissions-catalogue';

/** worker-api — the firm-scoped data plane. */
export const API_BASE = '/api/v1';
/** worker-auth — the identity plane (no version segment; served unchanged by the shell). */
export const AUTH_BASE = '/auth';

/** The shape the backend `Error` schema surfaces (code + message, optional details). */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  /** F-37: worker-api's per-request correlation id (also the `x-request-id` header). */
  request_id?: string;
}

/** A typed, non-revealing API failure (CC-7). Consumers narrow on `.status`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  /** Seconds from the `Retry-After` header when the server sent one (D6 503s). */
  readonly retryAfter?: number;
  /**
   * F-37: the request's correlation id — the `x-request-id` response header (worker-api
   * sets it on EVERY response; Cloudflare's `cf-ray` when present), else the error body's
   * `request_id`. Quote it in an error toast so support can find the request in the logs.
   * Absent on transport failures and on workers that do not set it.
   */
  readonly requestId?: string;
  /** Which worker answered: `auth` (worker-auth, the identity session) or `api` (worker-api,
   *  the firm session). The central 401 handler recovers differently for each. */
  origin?: 'api' | 'auth';

  constructor(status: number, body: ApiErrorBody, retryAfter?: number, requestId?: string) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    if (body.details !== undefined) this.details = body.details;
    if (retryAfter !== undefined && Number.isFinite(retryAfter)) this.retryAfter = retryAfter;
    const rid = requestId ?? body.request_id;
    if (rid) this.requestId = rid;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Narrowing guard used by the query cache + guards. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** A safe, human message for a caught error (mutations toast this — never a stack). */
export function getErrorMessage(err: unknown): string {
  if (isApiError(err)) return err.message;
  return 'Something went wrong. Please try again.';
}

interface RequestOptions {
  method?: string;
  /** JSON body — serialised + `content-type: application/json`. */
  json?: unknown;
  /** Raw body (e.g. a File/Blob for the logo upload) with its own content-type. */
  body?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Stamp the answering worker on a typed failure so the 401 handler can tell the sessions apart. */
function tagOrigin(err: unknown, origin: 'api' | 'auth'): never {
  if (isApiError(err) && err.origin === undefined) err.origin = origin;
  throw err;
}

/** A worker-api (`/api/v1`) request. */
function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return requestAt<T>(API_BASE, path, opts).catch((err: unknown) => tagOrigin(err, 'api'));
}

/** A worker-auth (`/auth`) request — `path` is relative to /auth (e.g. `/login`). */
function authRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return requestAt<T>(AUTH_BASE, path, opts).catch((err: unknown) => tagOrigin(err, 'auth'));
}

async function requestAt<T>(base: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...opts.headers };
  let body: BodyInit | undefined = opts.body;
  if (opts.json !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.json);
  }

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch {
    // Network/offline — a non-revealing transport error (status 0).
    throw new ApiError(0, { code: 'network_error', message: 'Network request failed.' });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const errBody: ApiErrorBody =
      parsed && typeof parsed === 'object' && 'code' in parsed && 'message' in parsed
        ? (parsed as ApiErrorBody)
        : { code: 'http_error', message: `Request failed (${res.status}).` };
    const retryAfterRaw = res.headers.get('retry-after');
    throw new ApiError(
      res.status,
      errBody,
      retryAfterRaw !== null ? Number.parseInt(retryAfterRaw, 10) : undefined,
      res.headers.get('x-request-id') ?? undefined,
    );
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Serialise a flat param bag into a `?a=1&b=2` string, dropping `undefined` values. */
function toQuery(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ── Wire shapes (mirrors of the OpenAPI schemas the SPA consumes) ──────────────

/** The identity worker-auth signs in (pre-firm: NO role, NO user_id — those are per firm). */
export interface IdentitySummary {
  identity_id: string;
  email: string;
  full_name: string;
}
/** `POST /auth/login` success (fixture: login-success). Mints ONLY the identity session. */
export interface LoginSuccess {
  identity: IdentitySummary;
  totp_enrolment_required: boolean;
}
export interface TotpRequiredResponse {
  totp_required: true;
}
export interface ForcePasswordChangeResponse {
  force_password_change: true;
  reason: string;
}
export type LoginResponse = LoginSuccess | TotpRequiredResponse | ForcePasswordChangeResponse;

/** Authoritative firm lifecycle status (mirrors the control-plane `firms.status` enum). */
export type FirmStatus = 'trial' | 'active' | 'suspended' | 'terminated';

/**
 * One row of the identity firm-selector feed (worker-auth `GET /auth/firms`; fixtures:
 * firms, firms-firm-name-unresolved). The identity's ACTIVE memberships — the authorization
 * GRANT set that gates which firm-scoped sessions may be minted.
 *
 * `firm_name` is the firm's display name, OPTIONAL: worker-auth omits it when it cannot be
 * resolved, and the picker then shows the `firm_id`. `firm_status` is NOT served today (the
 * select POST is the fail-closed status authority — a suspended/terminated firm 403s there);
 * an absent status is treated as selectable.
 */
export interface FirmMembership {
  firm_id: string;
  binding_slot: number;
  role: Role;
  firm_name?: string;
  firm_status?: FirmStatus;
}
/** `GET /auth/firms` response — the identity's active firm memberships. */
export interface FirmsListResponse {
  firms: FirmMembership[];
}
/** `POST /auth/firms/{firm_id}/select` success — the minted firm session's binding. */
export interface FirmSelectResponse {
  firm_id: string;
  binding_slot: number;
  role: Role;
}

/**
 * One LIVE invitation addressed to the signed-in identity (worker-auth `GET /auth/invitations`;
 * fixtures: invitations, invitations-empty). The firm picker lists these under the memberships
 * so an invitee can accept without the emailed token. `expires_at` is unix SECONDS. `firm_name`
 * is OPTIONAL — omitted when unresolvable, exactly like `FirmMembership`.
 */
export interface PendingInvitation {
  invitation_id: string;
  firm_id: string;
  role: Role;
  expires_at: number;
  firm_name?: string;
}
/** `GET /auth/invitations` response — the identity's live invitations. */
export interface PendingInvitationsResponse {
  invitations: PendingInvitation[];
}

/** An authentication method recorded on an identity session. */
export type Amr = 'pwd' | 'totp' | 'recovery_code';

/**
 * worker-auth `GET /auth/me` (fixture: me) — the IDENTITY profile. Identity-scoped: it has
 * no per-firm `role` or `user_id` (see {@link FirmPrincipal}).
 */
export interface IdentityProfile {
  identity_id: string;
  email: string;
  full_name: string;
  totp_enrolled: boolean;
  amr: Amr[];
}

/**
 * worker-api `GET /api/v1/auth/me` under the `argus_firm` session — the FIRM principal every
 * data route authorizes against (bound by worker-api's bind-equality gate). The SPA's ONLY
 * source of the per-firm `role` and `user_id`, so the UI can never grant an affordance the
 * data plane would refuse. 404/401 when no (valid) firm session is bound.
 */
export interface FirmPrincipal {
  user_id: string;
  email: string;
  full_name: string;
  role: Role;
  totp_enrolled: boolean;
  session_kind?: 'main' | 'admin';
  /** The bound firm (additive; `logo_url` is a worker-signed 24 h URL, null when no logo). */
  firm?: PrincipalFirm;
}
export interface PrincipalFirm {
  firm_id: string;
  slug: string;
  legal_name: string;
  logo_url: string | null;
}

export interface TotpEnrolResponse {
  secret: string;
  otpauth_uri: string;
}
export interface TotpConfirmResponse {
  ok: boolean;
  enrolled: boolean;
}
export interface RecoveryCodesResponse {
  recovery_codes: string[];
}
/**
 * worker-auth `GET /auth/invite/{token}` (fixtures: invite-metadata-*). `firm_name` is
 * OPTIONAL (omitted, never a placeholder, when unresolvable). `account_exists` picks the flow:
 * false → create the account (full name + password); true → sign in as that account, then accept.
 */
export interface InviteMetadata {
  email: string;
  role: Role;
  firm_id: string;
  firm_name?: string;
  expires_at: number;
  account_exists: boolean;
}
/** worker-auth `POST /auth/invite/{token}/accept` success (fixtures: invite-accept-*). */
export interface InviteAcceptResponse {
  identity: IdentitySummary;
  firm_id: string;
  role: Role;
  totp_enrolment_required: boolean;
}
/** Why an existing-account accept was refused (401 `invite_sign_in_required`, `details.reason`). */
export type InviteSignInReason =
  | 'session_required'
  | 'identity_mismatch'
  | 'second_factor_required';
/**
 * A TOTP-management refusal the UI handles by name (worker-auth `routes/totp.ts`; see
 * {@link totpRefusal}):
 *  - `second_factor_required` — 401: the identity session has not proven the enrolled factor
 *    (a password-only session). Returned by `reenrol`, by `confirm` on an already-enrolled
 *    identity, and by `recovery-codes` (fixture: totp-reenrol-second-factor-required). The fix is
 *    to sign in again with the authenticator or a recovery code.
 *  - `conflict` — 409: nothing to change (`confirm` on an enrolled identity with no rotation
 *    pending, or a concurrent confirm that already activated/promoted; `enrol`/`reenrol` on the
 *    wrong enrolment state).
 *  - `account_locked` — 429: `confirm` AND `reenrol` share login's per-identity failure window
 *    (fixtures: totp-confirm-account-locked, totp-reenrol-account-locked — the same body).
 *    `confirm` is refused before any code is verified; `reenrol` before the step-up password
 *    is verified, so the step-up can never be an unbounded password oracle. The same lock
 *    holds sign-in shut, so "sign in again" is NOT the remedy — only waiting is.
 */
export type TotpRefusal = 'second_factor_required' | 'conflict' | 'account_locked';
export interface FirmSettings {
  logo_r2_key?: string | null;
  /**
   * F-29: a browser-loadable (same-origin, session-gated, cache-busted) URL for the
   * current logo — `GET /firms/logo?v=…` — or null when none is uploaded. Served by the
   * API on reads; ignored on `updateSettings` writes.
   */
  logo_url?: string | null;
  primary_colour?: string | null;
  secondary_colour?: string | null;
  settings?: Record<string, unknown>;
}
/** One row of the firm-user directory (OpenAPI `FirmUser`; `GET /users`). Identity/
 *  role/state only — never credential material (CC-4). */
export interface FirmUser {
  user_id: string;
  email: string;
  full_name: string;
  role: Role;
  is_active: boolean;
  totp_enrolled: boolean;
}
/** `GET /users/assignable` — the people a case can be staffed with (cases.assign). */
export interface AssignableUser {
  user_id: string;
  email: string;
  full_name: string;
  role: Role;
}
export interface LogoUploadResponse {
  logo_r2_key: string;
  /** The signed URL for the freshly uploaded logo (shell mark refresh without a reload). */
  logo_url?: string | null;
}
export interface OkResponse {
  ok: boolean;
  reset_token?: string;
}
/** `GET /auth/password/reset/{token}` (F-34) — the masked account a live reset link is for. */
export interface ResetTokenHint {
  /** `a***@example.com` — first local-part character + the full domain. */
  email_hint: string;
}

// ── Cases (Sprint 2 / T-2.02) — mirrors of the OpenAPI Case surface ────────────

/** Case lifecycle status (Case.status enum). */
export type CaseStatus = 'open' | 'in_review' | 'closed' | 'archived';
/** Case client classification (Case.client_type enum). */
export type CaseClientType = 'corporate' | 'business' | 'individual' | 'partnership';
/** V1 engagement types (Case.engagement_type enum). */
export type EngagementType =
  | 'statement_analysis'
  | 'forensic_audit'
  | 'loan_audit'
  | 'tax_audit'
  | 'aml'
  | 'litigation_support'
  | 'multi_purpose';
/** Case-scoped membership role — distinct from the firm-wide `Role`. */
export type CaseRole = 'lead' | 'reviewer' | 'contributor' | 'observer';
/** Computed risk band (Case.risk_band enum). */
export type RiskBand = 'low' | 'medium' | 'high' | 'critical';

/** A case-scoped member as embedded in `Case.assigned_users`. */
export interface CaseAssignedUser {
  user_id: string;
  role: CaseRole;
  full_name?: string;
  email?: string;
}

/** The Case resource (OpenAPI `Case`). Required fields are non-optional here. */
export interface Case {
  id: string;
  case_number: string;
  /** Engagement title (backend `Case.title`; defaults to client_name when omitted on create). */
  title?: string;
  client_name: string;
  client_type: CaseClientType;
  engagement_type: EngagementType;
  status: CaseStatus;
  period_start: string;
  period_end: string;
  client_tin?: string | null;
  /** Free-text engagement description (PATCH-editable; nullable — null clears it). */
  description?: string | null;
  /** The user leading the engagement (PATCH-editable `led_by`; null = no lead set). */
  led_by?: string | null;
  risk_score?: number;
  risk_band?: RiskBand | null;
  assigned_users?: CaseAssignedUser[];
  created_at?: string;
  updated_at?: string;
  /**
   * PO finding 2026-10-05 ("a client entity") — the owning Client row. `client_id` and the
   * embedded `client` summary are additive alongside the legacy `client_name`/`client_type`
   * (kept for compatibility); both optional on the TS side so pre-existing fixtures without
   * them still type-check.
   */
  client_id?: string;
  client?: { client_id: string; name: string };
}

/** `GET /dashboard` (OpenAPI `DashboardResponse`) — the "My work" landing in one request. */
export type DashboardAlertKind =
  | 'unreconciled_statement'
  | 'manual_review'
  | 'failed_report'
  | 'self_review_pending'
  | 'open_annotation';

export interface DashboardCase {
  id: string;
  case_number: string;
  title?: string | null;
  client_name: string;
  engagement_type: EngagementType;
  status: CaseStatus;
  period_start: string;
  period_end: string;
  updated_at: string;
  risk_score?: number;
  risk_band?: RiskBand;
}

export interface DashboardActivity {
  timestamp: string;
  action: string;
  case_number: string;
  client_name: string;
  user: { user_id?: string; full_name?: string | null; email?: string | null };
}

export interface DashboardAlert {
  kind: DashboardAlertKind;
  case_number: string;
  client_name: string;
  count: number;
}

export interface DashboardResponse {
  cases: DashboardCase[];
  counts: { open: number; in_review: number; closed: number; archived: number };
  activity: DashboardActivity[];
  alerts: DashboardAlert[];
}

/** Pagination envelope (mirrors the OpenAPI `Pagination` shape / `/cases` list fields). */
export interface Pagination {
  page: number;
  per_page: number;
  total: number;
}

/** `GET /cases` response — a page of cases (inlines the pagination fields per contract). */
export interface CaseListResponse extends Pagination {
  cases: Case[];
}

/**
 * Query filters for `GET /cases`. Declared as a type alias (not an interface) so it
 * is assignable to `toQuery`'s `Record<string, …>` index signature.
 */
export type CaseListParams = {
  status?: CaseStatus | 'all';
  mine?: boolean;
  q?: string;
  page?: number;
  per_page?: number;
  /** F-03: sort key (default `created_at`). `risk_score` puts unscored cases last either way. */
  sort?: CaseSortKey;
  /** F-03: sort direction (default `desc`). */
  dir?: CaseSortDir;
};
/** The closed `GET /cases?sort=` set — anything else is a 400 `validation_error`. */
export type CaseSortKey = 'created_at' | 'updated_at' | 'risk_score';
export type CaseSortDir = 'asc' | 'desc';

/**
 * `POST /cases` request body. EITHER `client_id` (an existing Client) OR `client_name` +
 * `client_type` (+ optional `client_tin`) — the server creates the Client row for the latter
 * (PO finding 2026-10-05). All three are optional on the TS side so a caller can send one path
 * or the other; the client picker (components/clients/client-picker.tsx) normally resolves to
 * `client_id`, falling back to the free-text trio only when the clients list cannot load.
 */
export interface CreateCaseRequest {
  client_id?: string;
  client_name?: string;
  client_type?: CaseClientType;
  engagement_type: EngagementType;
  period_start: string;
  period_end: string;
  client_tin?: string | null;
  assigned_user_ids?: string[];
}

/**
 * `PATCH /cases/{caseId}` request body (PO finding 2026-10-05 #1 — "Edit an open case").
 * Every field is optional: the Edit-case form sends ONLY what the user actually changed.
 * `description`/`led_by` are nullable — an explicit `null` clears them (omit the key to
 * leave it unchanged).
 */
export interface UpdateCaseRequest {
  title?: string;
  /** Re-point the case at a different (or newly-created) existing Client row. */
  client_id?: string;
  client_name?: string;
  client_type?: CaseClientType;
  engagement_type?: EngagementType;
  period_start?: string;
  period_end?: string;
  description?: string | null;
  led_by?: string | null;
  status?: CaseStatus;
  assigned_user_ids?: string[];
}

/** A row of `GET /cases/{caseId}/members`. */
export interface CaseMember {
  user_id: string;
  role: CaseRole;
  full_name?: string;
  email?: string;
  added_at?: string;
  removed_at?: string | null;
}

/** `GET /cases/{caseId}/members` response. */
export interface CaseMembersResponse {
  members: CaseMember[];
}

/** `POST /cases/{caseId}/members` request body. */
export interface AddCaseMemberRequest {
  user_id: string;
  role: CaseRole;
}

/** `GET /cases/{caseId}/dashboard` KPI counters. */
export interface CaseDashboard {
  documents_count?: number;
  transactions_count?: number;
  flags_count?: { red?: number; amber?: number; gold?: number };
  reports_count?: number;
  total_recovery_identified_kobo?: number;
  recent_activity?: Array<{
    timestamp?: string;
    user?: Record<string, unknown>;
    action?: string;
  }>;
}

// ── Clients (PO finding 2026-10-05 — "a client entity? what if multiple cases belong to one
// client?") — mirrors of the new /clients OpenAPI surface. A Case now points at a Client row
// (`Case.client_id`) instead of carrying only free-text client_name/client_type.

/** Client classification (Client.client_type enum) — identical to `CaseClientType`. */
export type ClientType = CaseClientType;

/** One row of `GET /clients` (OpenAPI `ClientSummary`); ordered by name. */
export interface ClientSummary {
  client_id: string;
  name: string;
  client_type: ClientType;
  case_count: number;
  open_case_count: number;
  last_case_at: string | null;
}

/** `GET /clients` response — a page of clients. */
export interface ClientListResponse {
  clients: ClientSummary[];
  page: number;
  page_size: number;
  total: number;
}

/** Query filters for `GET /clients` (type alias for `toQuery` assignability). `q` matches
 *  name, case-insensitive. */
export type ClientListParams = {
  q?: string;
  page?: number;
  page_size?: number;
};

/** `GET /clients/{clientId}` response — the client (TIN masked) + the case rows the caller
 *  may see (same shape as a `GET /cases` item). */
export interface ClientDetail {
  client: {
    client_id: string;
    name: string;
    client_type: ClientType;
    tin_masked: string | null;
    created_at: string;
  };
  cases: Case[];
}

/** `POST /clients` request body. */
export interface CreateClientRequest {
  name: string;
  client_type: ClientType;
  tin?: string;
}

/**
 * `PATCH /clients/{clientId}` request body. Every field is optional — the edit dialog sends
 * ONLY what changed; `tin` is nullable (an explicit `null` clears it).
 */
export interface UpdateClientRequest {
  name?: string;
  client_type?: ClientType;
  tin?: string | null;
}

// ── Documents (Sprint 3 / T-3.08…13) — mirrors of the OpenAPI Document surface ─

/** Pipeline state (Document.ocr_status enum). */
export type OcrStatus = 'pending' | 'processing' | 'done' | 'failed' | 'manual_review';
/** OCR engine (Document.ocr_engine enum). */
export type OcrEngine = 'document_ai' | 'tesseract' | 'trocr' | 'manual';
/**
 * Extraction-pipeline state (Document.extraction_status enum). Added for the PO's
 * 2026-10-05 live finding: OCR can finish and the classifier can tag a doc_type while
 * the EXTRACTOR still fails and dead-letters the job, and the old UI showed nothing —
 * no "extracting", no "failed", no reason. `not_applicable` covers every document the
 * extractor never runs against (V1: anything that isn't a bank statement).
 */
export type ExtractionStatus = 'not_applicable' | 'pending' | 'done' | 'failed';
/** The canonical 15-value doc_type taxonomy (F-137; Document.doc_type enum). */
export type DocType =
  | 'bank-statement'
  | 'invoice'
  | 'receipt'
  | 'payment-voucher'
  | 'purchase-order'
  | 'financial-statement'
  | 'payroll-register'
  | 'bank-advice'
  | 'offer-letter'
  | 'agreement'
  | 'restructure'
  | 'cheque'
  | 'id-doc'
  | 'handwritten-note'
  | 'unknown';

/** The Document resource (OpenAPI `Document`). Optional fields land as the
 *  pipeline progresses (classifier → doc_type; OCR → engine/confidence;
 *  self-review → reviewed_by/reviewed_at). */
export interface CaseDocument {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  ocr_status: OcrStatus;
  legal_hold: boolean;
  retention_until: string;
  uploaded_at: string;
  uploaded_by: string;
  doc_type?: DocType;
  doc_type_confidence?: number;
  page_count?: number;
  ocr_engine?: OcrEngine;
  ocr_confidence?: number;
  processed_at?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  /**
   * Additive (backend fix in step with this one, 2026-10-05): absent on any payload the
   * backend hasn't shipped the field for yet — treated identically to `not_applicable`
   * (`lib/format/document-status.ts`'s `extractionBadge`/`isPipelineInFlight`).
   */
  extraction_status?: ExtractionStatus;
  extraction_error?: string | null;
  extraction_attempted_at?: string | null;
}

/** `GET /cases/{caseId}/documents` response. */
export interface DocumentListResponse {
  documents: CaseDocument[];
  pagination: Pagination;
}

/** Query filters for the documents list (type alias for `toQuery` assignability). */
export type DocumentListParams = {
  doc_type?: DocType;
  ocr_status?: OcrStatus;
  page?: number;
  per_page?: number;
};

/**
 * `POST /cases/{caseId}/documents/{docId}/reextract` (PO finding 2026-10-05 #6 —
 * "Re-run extraction") 202 success — the document's transactions are rebuilt from the
 * existing OCR transcript. 409 (OCR not done) surfaces as the standard typed `ApiError`.
 */
export interface ReextractResponse {
  ok: true;
  transactions_removed: number;
}

/** `POST /cases/{caseId}/documents` (initiate) response — the D2 worker-signed PUT URL. */
export interface InitiateUploadResponse {
  doc_id: string;
  upload_url: string;
  expires_at: string;
}

/** One page of an OCR transcript (`GET …/transcript`). */
export interface TranscriptPage {
  page_number?: number;
  text?: string;
  page_confidence?: number;
}
export interface Transcript {
  document_id: string;
  engine: OcrEngine;
  confidence?: number;
  pages: TranscriptPage[];
}

/** A share-target inbox receipt's display metadata (`GET /share-target/inbox`). */
export interface InboxItemMeta {
  inbox_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  received_at: string;
  attached_case_id: string | null;
  attached_at: string | null;
}
export interface InboxMetaResponse {
  items: InboxItemMeta[];
}

// ── Statement analysis (Sprint 4 / T-4.09/T-4.16 · US-030/031/032) ──────────────
// Mirrors of the OpenAPI Transaction / RiskFlag / Finding schemas + the
// statement-analysis response envelopes the Batch-D API (statement.ts) serialises.
// Money is ALWAYS signed int64 kobo (CC-6) — the SPA renders every kobo value
// through `formatNaira`, never a raw locale string.

/** Traffic-light band on a transaction risk flag (RiskFlag.severity — DISTINCT from
 *  Finding.severity per F-139). */
export type RiskFlagSeverity = 'red' | 'amber' | 'gold';

/** A statutory citation attached to a RiskFlag. */
export interface RiskFlagCitation {
  instrument: string;
  section: string;
}

/** The api-contract `RiskFlag` object the statement-analysis table renders directly. */
export interface RiskFlag {
  severity: RiskFlagSeverity;
  type: string;
  detail: string;
  citation?: RiskFlagCitation;
}

/** The 34 transaction categories (OpenAPI `Transaction.category` enum, B.1 / registry Part 11 + 11A). */
export const TRANSACTION_CATEGORIES = [
  'account-maintenance',
  'airtime-data',
  'atm-fee',
  'atm-withdrawal',
  'bank-charge',
  'bill-payment',
  'card-fee',
  'cash-withdrawal',
  'cashless-deposit',
  'cashless-withdrawal',
  'cheque-clearing',
  'cot',
  'dividend',
  'ebill-fee',
  'ec',
  'fx-charge',
  'interest-charge',
  'interest-credit',
  'loan-disbursement',
  'loan-repayment',
  'management-fee',
  'ofa-fee',
  'pos-cash-withdrawal',
  'pos-purchase',
  'processing-fee',
  'reversal',
  'salary',
  'savings-sweep',
  'sms-alert',
  'stamp-duty',
  'tax',
  'transfer-in',
  'transfer-out',
  'unclassified',
] as const;
export type TransactionCategory = (typeof TRANSACTION_CATEGORIES)[number];

/** The Transaction resource (OpenAPI `Transaction`; serializeTxn in statement.ts). */
export interface Transaction {
  id: string;
  document_id: string;
  case_id?: string;
  /** Posting date, `YYYY-MM-DD`. */
  posted_date: string;
  value_date: string | null;
  description: string;
  /** Single SIGNED int64 kobo: negative = debit, positive = credit (CC-6). */
  amount_kobo: number;
  balance_kobo: number | null;
  reference: string | null;
  category: TransactionCategory | string | null;
  category_confidence: number | null;
  counterparty: string | null;
  counterparty_id: string | null;
  is_round: boolean;
  is_structured: boolean;
  flags: RiskFlag[];
}

/** `GET /cases/{caseId}/transactions` response. */
export interface TransactionListResponse {
  transactions: Transaction[];
  pagination: Pagination;
}

/** Query filters for the transactions list (type alias for `toQuery` assignability). */
export type TransactionListParams = {
  page?: number;
  per_page?: number;
  /** Inclusive lower bound on posted_date, `YYYY-MM-DD`. */
  from?: string;
  /** Inclusive upper bound on posted_date, `YYYY-MM-DD`. */
  to?: string;
  category?: string;
  counterparty?: string;
  amount_kobo_min?: number;
  amount_kobo_max?: number;
  has_flag?: boolean;
  q?: string;
  document_id?: string;
};

/** Forensic-grade finding severity (Finding.severity — DISTINCT from RiskFlag band). */
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
/** Finding lifecycle status (Finding.status enum). */
export type FindingStatus = 'open' | 'accepted' | 'disputed' | 'resolved' | 'suppressed';

/** The Finding resource (OpenAPI `Finding`; serializeFinding in statement.ts). */
export interface Finding {
  id: string;
  case_id: string;
  document_id: string | null;
  transaction_id: string | null;
  finding_type: string;
  severity: FindingSeverity;
  title: string;
  description: string;
  /** The statutory citation (CBN guide + section text), or null. */
  statutory_basis: string | null;
  /** Monetary impact in int64 kobo (excess / penalty), or null. */
  amount_kobo: number | null;
  status: FindingStatus;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  created_by_user_id: string | null;
  created_by_system: boolean;
}

/**
 * An `excess_charge` Finding enriched with the DISCRETE US-031 comparison (OpenAPI
 * `ExcessChargeFinding`). The server derives these at read time from the source
 * transaction + CBN reference reader; each is nullable (graceful degradation). Money is
 * int64 kobo — the SPA renders it through `formatNaira`.
 */
export interface ExcessChargeFinding extends Finding {
  /** Source transaction posted_date (`YYYY-MM-DD`) — the CHARGE date, NOT `created_at`. */
  charge_date: string | null;
  /** The fee actually charged (int64 kobo). */
  amount_charged_kobo: number | null;
  /** The period-aware CBN cap in force at the charge date (int64 kobo). */
  expected_max_kobo: number | null;
  /** The recoverable excess (charged − cap; = the finding amount) (int64 kobo). */
  excess_kobo: number | null;
}

/**
 * A `cashless_breach` Finding enriched with the DISCRETE US-032 comparison (OpenAPI
 * `CashlessBreachFinding`). Derived at read time; each field nullable.
 */
export interface CashlessBreachFinding extends Finding {
  /** Source transaction posted_date (`YYYY-MM-DD`) — the CHARGE date, NOT `created_at`. */
  charge_date: string | null;
  /** The cash transaction amount (int64 kobo). */
  amount_charged_kobo: number | null;
  /** The period-aware cashless threshold in force at the charge date (int64 kobo). */
  threshold_kobo: number | null;
  /** The amount above the threshold (amount − threshold) (int64 kobo). */
  excess_kobo: number | null;
  /** The penalty rate as a FRACTION (0.03 = 3%) — a multiplier, not money. */
  rate: number | null;
  /** The penalty exposure (= the finding amount) (int64 kobo). */
  penalty_kobo: number | null;
}

/** `GET …/statement-analysis/excess-charges` — findings + Σ open excess (US-031). */
export interface ExcessChargesResponse {
  findings: ExcessChargeFinding[];
  total_excess_kobo: number;
}

/** The read-time cashless status of one cash transaction (OpenAPI `CashlessTransaction.status`). */
export type CashlessTxnStatus = 'breach' | 'within_threshold' | 'no_threshold_in_force';

/**
 * One cash-category transaction in the US-032 all-cash overlay (OpenAPI `CashlessTransaction`),
 * annotated with its read-time cashless status. A READ overlay — not a finding. Money is int64
 * kobo — the SPA renders it through `formatNaira`.
 */
export interface CashlessTransaction {
  transaction_id: string;
  posted_date: string;
  txn_type: 'deposit' | 'withdrawal';
  counterparty: string | null;
  /** The cash amount magnitude assessed against the threshold (int64 kobo). */
  amount_kobo: number;
  /** The period/tier-aware threshold in force; null pre-2019 (no threshold in force). */
  threshold_kobo: number | null;
  /** amount − threshold for a breach; 0 otherwise (int64 kobo). */
  excess_kobo: number;
  /** round(excess × rate) for a breach; 0 otherwise (int64 kobo). */
  penalty_kobo: number;
  breaches: boolean;
  status: CashlessTxnStatus;
}

/**
 * `GET …/statement-analysis/cashless-policy` — the breach findings + Σ open penalty, PLUS
 * `all_transactions`: every cash-category txn in the case period with its cashless status (US-032).
 */
export interface CashlessPolicyResponse {
  findings: CashlessBreachFinding[];
  total_penalty_kobo: number;
  all_transactions: CashlessTransaction[];
}

/** `GET …/statement-analysis/large-transactions` — top-N largest + structuring suspects. */
export interface LargeTransactionsResponse {
  largest: Transaction[];
  structuring_suspects: Transaction[];
}

/** `GET …/statement-analysis/anomalies` — the shipped anomaly types + honestly deferred ones. */
export interface AnomaliesResponse {
  round_number: Transaction[];
  structuring: Transaction[];
  /** Anomaly types not yet computable in Batch D (off_hours, repeated_counterparty). */
  deferred: string[];
}

/** One per-statement reconciliation row (`GET …/statement-analysis/reconciliation`). */
export interface ReconciliationStatement {
  document_id: string;
  opening_balance_kobo: number | null;
  closing_balance_kobo: number | null;
  sum_debits_kobo: number;
  sum_credits_kobo: number;
  /** closing − (opening + net); null when opening/closing unknown. */
  residual_kobo: number | null;
  /** True when |residual| ≤ ₦100 tolerance (or not computable). */
  ok: boolean;
}
export interface ReconciliationResponse {
  statements: ReconciliationStatement[];
}

/** `GET …/transactions/summary` — aggregate KPIs (T-4.16). */
export interface TransactionSummary {
  total_credits_kobo: number;
  total_debits_kobo: number;
  count: number;
  largest_kobo: number;
  by_category: Array<{ category: string; count: number; total_kobo: number }>;
}

/** `POST …/statement-analysis/run` — the re-run pipeline result. */
export interface RunAnalysisResponse {
  findings_created: number;
  findings_updated: number;
  findings_retracted: number;
  findings_unchanged: number;
  findings_skipped_suppressed: number;
  risk_score: number;
  risk_band: RiskBand;
}

// ── Counterparties + sanctions (Sprint 4 / T-4.15 · US-130) ─────────────────────
// Mirrors of the OpenAPI Counterparty / CounterpartyDirectoryRow / SanctionsHit schemas
// + the counterparties + sanctions response envelopes. Money is ALWAYS int64 kobo (CC-6).

/** The four-value sanctions-list attribution (Counterparty.sanctions_flag / SanctionsHit.list). */
export type SanctionsFlag = 'ofac' | 'un' | 'efcc' | 'pep';

/** The base Counterparty resource (OpenAPI `Counterparty`). Raw tin/bvn/bank_account are
 *  NEVER sent by the API (CC-4), so they are absent from this shape. */
export interface Counterparty {
  id: string;
  normalised_name?: string;
  display_name: string;
  /** ofac|un|efcc|pep, or null when screened clean / not yet screened. */
  sanctions_flag: SanctionsFlag | null;
  notes: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

/** A per-case directory row (OpenAPI `CounterpartyDirectoryRow`) — a Counterparty enriched
 *  with the case-scoped int64 aggregates (in/out totals, txn count, first/last txn date). */
export interface CounterpartyDirectoryRow extends Counterparty {
  /** Σ credit (positive) amount_kobo for this counterparty in the case. */
  in_kobo: number;
  /** Absolute Σ debit (negative) amount_kobo. */
  out_kobo: number;
  transaction_count: number;
  first_transaction_date: string | null;
  last_transaction_date: string | null;
}

/** `GET /cases/{caseId}/counterparties` response. */
export interface CounterpartyListResponse {
  counterparties: CounterpartyDirectoryRow[];
  pagination: Pagination;
}

/** Query filters for the directory list (type alias for `toQuery` assignability). */
export type CounterpartyListParams = {
  page?: number;
  per_page?: number;
  q?: string;
  sanctions_only?: boolean;
};

/** `PATCH /cases/{caseId}/counterparties/{id}` request body. */
export interface CounterpartyPatch {
  display_name?: string;
  notes?: string | null;
}

/** A single sanctions match (OpenAPI `SanctionsHit`). `score` is the L.2 confidence [0,1]. */
export interface SanctionsHit {
  list: SanctionsFlag;
  matched_name: string;
  score: number;
  list_entry_id?: string;
  list_url?: string;
}

/** `POST /sanctions/screen` response — the ad-hoc hits + the outage/staleness signal. */
export interface SanctionsScreenResponse {
  hits: SanctionsHit[];
  stale: boolean;
  refreshed_at: string | null;
}

/** `POST /sanctions/screen` request body (name/TIN/BVN query, half-open threshold). */
export interface SanctionsScreenRequest {
  query: string;
  type?: 'name' | 'tin' | 'bvn';
  threshold?: number;
}

/** `GET /sanctions/hits/{counterpartyId}` response — the cached per-counterparty hits. */
export interface SanctionsHitsResponse {
  hits: SanctionsHit[];
}

// ── Audit trail (Sprint 6 / T-6.14 · US-080) ────────────────────────────────────
// Mirrors of the OpenAPI AuditEvent + verify/integrity shapes. Every §2.1
// hash-input field arrives VERBATIM AS STORED (`payload_json` is the canonical
// string; `timestamp_ms` the exact unix-ms hash input) so the per-event modal
// re-computes `row_hash` client-side with the canonical @argus/audit
// implementation (CC-11 — the client re-compute must match the server's hash).

/** One stored audit_events row (OpenAPI `AuditEvent`). */
export interface AuditEventWire {
  event_id: string;
  sequence: number;
  event_type: string;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_session_id: string | null;
  case_id: string | null;
  resource_type: string | null;
  resource_id: string | null;
  /** The STORED canonical payload string — a hash input, never re-serialised. */
  payload_json: string;
  ip_address: string | null;
  user_agent: string | null;
  timestamp: string;
  /** unix ms — the exact §2.1 hash input. */
  timestamp_ms: number;
  prev_hash: string;
  row_hash: string;
  anchored_in: string | null;
}

export interface AuditListResponse {
  events: AuditEventWire[];
  pagination: { page: number; per_page: number; total: number };
}

/** Query filters for the case audit list (type alias for `toQuery`). */
export type AuditListParams = {
  from?: string;
  to?: string;
  user_id?: string;
  action_type?: string;
  page?: number;
  per_page?: number;
};

/** `POST /cases/{caseId}/audit/verify` — the server-side range verdict. */
export interface AuditVerifyResponse {
  ok: boolean;
  events_verified: number;
  case_events: number;
  range: { sequence_from: number; sequence_to: number } | null;
  first_tamper_sequence: number | null;
  first_tamper_at: string | null;
  reason: string | null;
  polygon_anchors: unknown[];
  on_chain: string;
}

/** `GET /audit/integrity` — the last daily worker-integrity-check verdict. */
export interface AuditIntegrityResponse {
  status: 'passed' | 'failed' | 'never_run';
  ok: boolean | null;
  checked_at: string | null;
  events_verified: number | null;
  first_tamper_sequence: number | null;
}

// ── Reference data (Sprint 6 / T-6.05/T-6.06 · US-120/121/122) ──────────────────
// Mirrors of the /reference/* wire shapes (workers/api routes/reference.ts, shipped
// Batch A). Every endpoint takes an optional `date` ('in force on this date'; absent
// = today). Money bounds are int64 kobo (CC-6) → rendered via formatNaira; `rate_pct`
// values are NOT money (percent points on MPR/PIT/CIT, fractions on WHT/dev-levy) and
// render as percentages, never through formatNaira.

/** The company-class fork the CIT/dev-levy reference asks take (`applies_to`). */
export type CompanyClass = 'large_company' | 'small_company';

/** One CBN-guide registry row (`GET /reference/cbn-guides`). */
export interface CbnGuide {
  id: string;
  title: string | null;
  valid_from: string | null;
  valid_to: string | null;
  superseded_by: string | null;
  scope: string | null;
  usage: string | null;
}
export interface CbnGuidesResponse {
  guides: CbnGuide[];
  count: number;
}

/** `GET /reference/mpr` — the monetary-policy rate in force (percent points). */
export interface MprResponse {
  rate_pct: number;
  valid_from: string;
  valid_to: string | null;
  source: string;
}

/** One WHT-schedule row (`GET /reference/wht-rates`). `rate_pct` is a FRACTION (0.05 = 5%). */
export interface WhtRate {
  id: string;
  category: string;
  description: string;
  rate_pct: number;
  applies_to: string;
  effective_from: string;
  effective_to: string | null;
  source_citation: string;
  notes: string | null;
}
export interface WhtRatesResponse {
  rates: WhtRate[];
  count: number;
}

/** One PIT band (`GET /reference/pit-bands`). Bounds are int64 kobo (CC-6). */
export interface PitBand {
  regime: string;
  band_number: number;
  lower_bound_kobo: number;
  upper_bound_kobo: number | null;
  rate_pct: number;
  source_citation: string;
}

/** `GET /reference/cit-rates` — the CIT rate for a company class (percent points). */
export interface CitRateResponse {
  applies_to: CompanyClass;
  rate_pct: number;
  source: string;
  notes: string | null;
}

/** One stamp-duty instrument (`GET /reference/stamp-duty`). `computation` is the seeded
 *  encoding ('ad_valorem:<fraction>' | 'flat:<kobo>kobo'); `min_amount_kobo` is int64 kobo. */
export interface StampDutyInstrument {
  id: string;
  instrument_type: string;
  computation: string;
  min_amount_kobo: number | null;
  effective_from: string;
  effective_to: string | null;
  source_citation: string;
}
export interface StampDutyResponse {
  instruments: StampDutyInstrument[];
  count: number;
}

/** `GET /reference/dev-levy` — the component array + summed total (`total_pct` a fraction). */
export interface DevLevyResponse {
  applies_to: CompanyClass;
  components: Array<{ component: string; rate_pct: number; source_citation: string }>;
  total_pct: number;
  exempt: boolean;
}

/** `GET /reference/statutes/{citation}` — one directory entry (US-121; directory, not full text). */
export interface Statute {
  id: string;
  short_title: string;
  citation: string | null;
  category: string;
  effective_from: string;
  status: string;
  /** Echoed section suffix when the citation carried one ('NTAA-2025-s31' → 's31'). */
  section: string | null;
}

/**
 * `GET /reference/statutes` — the statute-directory LIST (US-121; category-filterable).
 *
 * Mounted in `workers/api/src/routes/reference.ts` over the seeded control-plane statute
 * directory (`listStatutes()` in `packages/backend/lib/reference-browse.ts`), requireAuth-
 * only like every other reference read; in the OpenAPI contract and covered by
 * `reference.inprocess.test.ts`. (The earlier "backend gap" note here was stale — F-26.)
 */
export type StatuteListItem = Omit<Statute, 'section'>;
export interface StatuteListResponse {
  statutes: StatuteListItem[];
  count: number;
}

// ── Reports (Sprint 6 / T-6.10 · US-070) ────────────────────────────────────────
// Mirrors of the `/cases/{caseId}/reports/*` wire shapes documented in the T-6.10
// OpenAPI spec-refs + the `serializeReport()` shape already in routes/reports.ts.
//
// ⚠️ BACKEND GAP (flagged, not invented): only `POST /court-bundle` and
// `POST /reports/{reportId}/sign` shipped in Batch C. The report CRUD surface this
// UI consumes — GET list, POST generate, GET/DELETE one, download (302), preview,
// regenerate, verify — is NOT yet implemented in `routes/reports.ts` and is absent
// from the OpenAPI contract. This client + the Reports UI are the frontend half of
// T-6.10; they are tested against faked responses (no live backend) and will 404
// until the backend `/reports/*` routes + OpenAPI sections land.

/** The 8 renderable report types (queues.ts REPORT_TYPES; crypto_forensic is V2 per Q12). */
export const REPORT_TYPES = [
  'statement_analysis',
  'forensic',
  'link_analysis',
  'cashless_policy',
  'loan_audit',
  'recovery_letter',
  'compliance',
  'tax_audit',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

/** A rendered-artefact format (queues.ts REPORT_FORMATS). */
export type ReportFormat = 'pdf' | 'docx' | 'html';

/** Report render lifecycle (reports.status enum). */
export type ReportStatus = 'generating' | 'completed' | 'failed';

/** The Report resource (OpenAPI `Report`; `serializeReport` in routes/reports.ts). */
export interface Report {
  id: string;
  case_id: string;
  report_type: ReportType | 'court_bundle';
  title: string;
  version: number;
  status: ReportStatus;
  artefacts: {
    html_r2_key: string | null;
    pdf_r2_key: string | null;
    docx_r2_key: string | null;
    sha256: string | null;
  };
  is_court_bundle: boolean;
  evidence_certificate_r2_key: string | null;
  superseded_by: string | null;
  superseded_at: string | null;
  generated_at: string;
  generated_by: string;
}

/** `GET /cases/{caseId}/reports` response — the version series (all rows, newest first). */
export interface ReportListResponse {
  reports: Report[];
}

/** `POST /cases/{caseId}/reports` request body (US-070; `formats` defaults to [html, pdf]). */
export interface GenerateReportRequest {
  report_type: ReportType;
  formats?: ReportFormat[];
}

/** `POST /cases/{caseId}/reports/preview` response — in-page HTML, no R2 write. */
export interface ReportPreviewResponse {
  html: string;
}

/** `POST /cases/{caseId}/reports/{reportId}/verify` response (anchor inert in Sprint 6). */
export interface ReportVerifyResponse {
  sha256_match: boolean;
  citations_resolved: boolean;
  anchor_status: 'unanchored' | 'pending' | 'confirmed';
}

/** AI job lifecycle (T-7.12 `ai_jobs.status`). */
export type AiJobStatus = 'queued' | 'succeeded' | 'failed';

/**
 * The AI summary job (T-7.12; OpenAPI `AiSummaryJob`). `summary` is the AI
 * executive-summary SUGGESTION and is populated only once `status` is `succeeded`.
 * DD-010: the SUGGESTION is a human-in-the-loop draft — it is NEVER the signed report
 * body; the UI presents it as a suggestion the user accepts/edits, never auto-inserted.
 */
export interface AiSummaryJob {
  job_id: string;
  report_id: string | null;
  case_id: string | null;
  job_type: 'summarise_report' | 'classify_doc' | 'nl_query';
  status: AiJobStatus;
  summary: string | null;
  model: string | null;
  provider_used: 'workers_ai' | 'gemini' | null;
  generated_at: string | null;
}

/**
 * PUT the file bytes to the worker-signed upload_url with per-file progress.
 *
 * Uses XMLHttpRequest, NOT fetch: fetch has no upload-progress events in any
 * shipping browser (request-body streams are still gated), and XHR's
 * `upload.onprogress` is exactly the 0..1 fraction the per-file progress bar
 * needs — the documented pragmatic choice. No credentials ride along: the URL
 * is token-authed (the HMAC token IS the authorisation, D2), so the session
 * cookie is irrelevant to it. Failures become the same typed ApiError the
 * fetch wrapper produces (CC-7).
 */
export function putToUploadUrl(
  uploadUrl: string,
  file: Blob,
  onProgress?: (fraction: number) => void,
): Promise<{ doc_id: string; sha256: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('content-type', file.type !== '' ? file.type : 'application/octet-stream');
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      const parsed = safeJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as { doc_id: string; sha256: string });
        return;
      }
      const errBody: ApiErrorBody =
        parsed && typeof parsed === 'object' && 'code' in parsed && 'message' in parsed
          ? (parsed as ApiErrorBody)
          : { code: 'http_error', message: `Upload failed (${xhr.status}).` };
      reject(new ApiError(xhr.status, errBody));
    };
    xhr.onerror = () =>
      reject(new ApiError(0, { code: 'network_error', message: 'Network request failed.' }));
    xhr.send(file);
  });
}

export function isTotpRequired(r: LoginResponse): r is TotpRequiredResponse {
  return 'totp_required' in r && r.totp_required === true;
}
export function isForcePasswordChange(r: LoginResponse): r is ForcePasswordChangeResponse {
  return 'force_password_change' in r && r.force_password_change === true;
}
export function isLoginSuccess(r: LoginResponse): r is LoginSuccess {
  const identity = (r as Partial<LoginSuccess>).identity;
  return (
    typeof identity === 'object' &&
    identity !== null &&
    typeof identity.identity_id === 'string' &&
    typeof identity.email === 'string'
  );
}

const INVITE_SIGN_IN_REASONS: readonly InviteSignInReason[] = [
  'session_required',
  'identity_mismatch',
  'second_factor_required',
];

/**
 * The refusal reason when an invite accept failed with worker-auth's 401
 * `invite_sign_in_required` (fixtures: invite-accept-sign-in-required-*), else null. An
 * unrecognised reason is null too — the caller then treats it as a generic failure.
 */
export function inviteSignInReason(err: unknown): InviteSignInReason | null {
  if (!isApiError(err) || err.status !== 401 || err.code !== 'invite_sign_in_required') return null;
  const reason = err.details?.['reason'];
  return INVITE_SIGN_IN_REASONS.find((r) => r === reason) ?? null;
}

/**
 * The named TOTP-management refusal carried by a failed `api.auth.totp*` call, else null (a
 * wrong password, a wrong/reused code, a network failure… — the caller shows its message).
 * Matched on status AND code, so an unrelated 401/409/429 is never mistaken for one.
 */
export function totpRefusal(err: unknown): TotpRefusal | null {
  if (!isApiError(err)) return null;
  if (err.status === 401 && err.code === 'second_factor_required') return 'second_factor_required';
  if (err.status === 409 && err.code === 'conflict') return 'conflict';
  if (err.status === 429 && err.code === 'account_locked') return 'account_locked';
  return null;
}

// ── Annotations (Sprint 7 / T-7.09; US-091) ────────────────────────────────────
/** A document/transaction-anchored annotation (OpenAPI `Annotation`). */
export interface Annotation {
  id: string;
  case_id: string;
  document_id: string | null;
  transaction_id: string | null;
  page_number: number | null;
  anchor: unknown | null;
  body: string;
  parent_annotation_id: string | null;
  status: 'open' | 'resolved';
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  created_by: string;
  /** F-08: the author's display name (LEFT-joined `users.full_name`; null if that row is gone). */
  created_by_name?: string | null;
  /** F-08: the author's email (LEFT-joined `users.email`; null if that row is gone). */
  created_by_email?: string | null;
  updated_at: string;
}
export interface AnnotationListResponse {
  annotations: Annotation[];
}
export interface CreateAnnotationInput {
  document_id?: string;
  transaction_id?: string;
  page_number?: number;
  anchor?: unknown;
  body: string;
  parent_annotation_id?: string;
}

// ── DSAR — firm-admin fulfilment (Sprint 7 / T-7.14; US-153) ────────────────────
// Mirrors the OpenAPI `DsarRequest` shape. The managing_partner-only subject-rights
// queue: export (access → a PII-safe JSON attachment), rectify (one allowlisted PII
// field), erase (case-scoped, subject to the legal-hold + retention-floor guards →
// 409). Gated by `dsar.read` (list) / `dsar.manage` (export/rectify/erase).

/** The allowlisted subject-rights request types (`DsarRequest.request_type`). */
export type DsarRequestType = 'access' | 'rectification' | 'erasure';
/** DSAR fulfilment lifecycle (`DsarRequest.status`). */
export type DsarStatus = 'submitted' | 'in_progress' | 'fulfilled' | 'refused';
/** The allowlisted rectifiable PII fields (`POST …/rectify` body.field; CC-4). */
export type DsarRectifyField = 'full_name' | 'email';

/** A subject-rights (DSAR) request row (OpenAPI `DsarRequest`). */
export interface DsarRequest {
  id: string;
  request_type: DsarRequestType;
  status: DsarStatus;
  case_number: string;
  subject_user_id: string;
  subject_email?: string;
  requested_by: string;
  requested_at: string;
  resolved_by?: string;
  resolved_at?: string;
  refusal_reason?: string;
  details?: string;
}

/** `GET /dsar/requests` response. */
export interface DsarListResponse {
  requests: DsarRequest[];
}

/** The `POST …/erase` 409 refusal reason (`Error.details.reason`). */
export type DsarEraseRefusalReason = 'legal_hold' | 'retention_floor' | 'already_fulfilled';

/** The API surface. Grouped by resource; each returns the typed response or throws ApiError. */
export const api = {
  /** worker-auth — the identity plane (`/auth/*`). */
  auth: {
    login: (body: { email: string; password: string; totp_code?: string }) =>
      authRequest<LoginResponse>('/login', { method: 'POST', json: body }),
    /**
     * Recovery-code sign-in (the "lost TOTP only" path). Sent as a login variant
     * carrying `recovery_code` in place of `totp_code`. The server consumes the code
     * single-use (Argon2id) as the second factor and mints a session on success
     * (auth-and-authz.md §1.6).
     */
    loginWithRecovery: (body: { email: string; password: string; recovery_code: string }) =>
      authRequest<LoginResponse>('/login', { method: 'POST', json: body }),
    /** The signed-in IDENTITY (worker-auth). No per-firm role — see `firmMe`. */
    me: () => authRequest<IdentityProfile>('/me'),
    /**
     * The FIRM principal of the bound firm session (worker-api `GET /api/v1/auth/me`, under
     * the `argus_firm` cookie) — the source of the per-firm role + user_id.
     */
    firmMe: () => request<FirmPrincipal>('/auth/me'),
    /**
     * The identity firm-selector feed (worker-auth `GET /auth/firms`; ADR-0022 B7/B10) —
     * the active memberships a firm-scoped session may be minted for.
     */
    firms: () => authRequest<FirmsListResponse>('/firms'),
    /**
     * Mint a firm-scoped session for one membership (worker-auth
     * `POST /auth/firms/{firm_id}/select`; the R1 chokepoint) → sets the compound
     * HttpOnly `argus_firm` cookie the SPA never sees. A non-membership or a
     * suspended/terminated firm is a non-revealing 403 (CC-7); the session binds to
     * exactly this one firm (never an UPDATE — "switch firm" re-selects for a new mint).
     */
    selectFirm: (firmId: string) =>
      authRequest<FirmSelectResponse>(`/firms/${encodeURIComponent(firmId)}/select`, {
        method: 'POST',
        json: {},
      }),
    /**
     * The identity's LIVE invitations (worker-auth `GET /auth/invitations`; fixtures:
     * invitations, invitations-empty) — the firms it has been invited to but not yet joined,
     * listed on the picker so it can accept from there (PO ruling 2026-09-23).
     */
    invitations: () => authRequest<PendingInvitationsResponse>('/invitations'),
    /**
     * Accept one of the identity's own invitations by id (worker-auth
     * `POST /auth/invitations/{invitation_id}/accept`; fixtures: invitation-accept,
     * invitation-accept-not-found). Authorised by the signed-in identity session at its enrolled
     * factor strength — the SAME grant the token accept performs, so it answers with the same
     * body and the same 401 `invite_sign_in_required` refusals (see {@link inviteSignInReason}).
     * 404 when the invitation is not this identity's or is no longer live. Sends the JSON `{}`
     * body the CSRF guard's rule 3 requires (like `invites.accept` for an existing account).
     */
    acceptInvitation: (invitationId: string) =>
      authRequest<InviteAcceptResponse>(`/invitations/${encodeURIComponent(invitationId)}/accept`, {
        method: 'POST',
        json: {},
      }),
    /**
     * Sign out (fixture: logout). Always 200 `{ ok: true }` with both cookies expired — valid,
     * expired, revoked or absent session alike (worker-auth never 401s it).
     *
     * CSRF (worker-auth `lib/csrf-guard.ts`): every state-changing call below is a same-origin
     * fetch, and every one that carries a body sends `content-type: application/json`. The four
     * routes worker-auth reads NO body on — logout, totp/enrol, totp/recovery-codes and
     * firms/:id/select — are exempt from its JSON rule; the first three are sent body-less.
     * Pinned against the guard's own route list in worker-auth-contract.test.tsx.
     */
    logout: () => authRequest<OkResponse>('/logout', { method: 'POST' }),
    /** First-time enrolment: a PENDING secret (fixture: totp-enrol). 409 when already enrolled. */
    totpEnrol: () => authRequest<TotpEnrolResponse>('/totp/enrol', { method: 'POST' }),
    /**
     * Owed-#15 self-service rotation: step-up with the CURRENT password → a fresh
     * PENDING secret + URI (same once-only shape as enrol; fixture: totp-reenrol). The OLD
     * secret stays active until `totpConfirm` proves a live code from the NEW secret.
     * Requires a session that proved the enrolled factor (a TOTP or recovery-code sign-in):
     * a password-only session is refused 401 `second_factor_required` (fixture:
     * totp-reenrol-second-factor-required) before the password is checked. The step-up is
     * bounded by login's own SEC-3 window: wrong passwords here count towards the identity
     * lock, and a locked identity is refused 429 `account_locked` (fixture:
     * totp-reenrol-account-locked) before the password is verified at all.
     */
    totpReenrol: (body: { current_password: string }) =>
      authRequest<TotpEnrolResponse>('/totp/reenrol', { method: 'POST', json: body }),
    /**
     * Prove a live code (fixture: totp-confirm). First activation (identity NOT yet enrolled)
     * needs no prior factor and records `totp` on this session. On an ENROLLED identity it can
     * only promote a pending rotation: a password-only session → 401 `second_factor_required`;
     * nothing pending → 409 `conflict`. A locked identity → 429 `account_locked`; every wrong or
     * reused code → the same 400. See {@link totpRefusal}.
     */
    totpConfirm: (body: { code: string }) =>
      authRequest<TotpConfirmResponse>('/totp/confirm', { method: 'POST', json: body }),
    /**
     * Mint (and replace) the one-time recovery codes (fixture: totp-recovery-codes). Requires an
     * ENROLLED identity (409 otherwise) on a session that proved the factor (401
     * `second_factor_required` otherwise) — so the enrolment flow requests them only AFTER
     * `totpConfirm` succeeds (which records `totp` on the confirming session).
     */
    totpRecoveryCodes: () =>
      authRequest<RecoveryCodesResponse>('/totp/recovery-codes', { method: 'POST' }),
    forgotPassword: (body: { email: string }) =>
      authRequest<OkResponse>('/password/reset/request', { method: 'POST', json: body }),
    /**
     * F-34: the masked account a reset link is for (`{ email_hint: 'a***@example.com' }`),
     * so the reset form can say whose password it sets. 404 for an unknown, consumed or
     * expired token (one shape — not a lifecycle oracle). Does NOT consume the token.
     */
    resetTokenHint: (token: string) =>
      authRequest<ResetTokenHint>(`/password/reset/${encodeURIComponent(token)}`),
    resetPassword: (body: { token: string; new_password: string }) =>
      authRequest<OkResponse>('/password/reset/submit', { method: 'POST', json: body }),
  },
  /** worker-auth invites (`/auth/invite/*`). */
  invites: {
    get: (token: string) => authRequest<InviteMetadata>(`/invite/${encodeURIComponent(token)}`),
    /**
     * Accept. A NEW account sends `{ password, full_name }` (an identity session is minted); an
     * EXISTING account sends nothing and is authorised by its own signed-in identity session
     * (refused 401 `invite_sign_in_required` otherwise — see {@link inviteSignInReason}).
     */
    accept: (token: string, body?: { password: string; full_name: string }) =>
      authRequest<InviteAcceptResponse>(`/invite/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        json: body ?? {},
      }),
  },
  /** Firm-user admin surface (T-1.16-era list + the owed-#15 MP TOTP reset). */
  users: {
    /** `GET /users` — gated by `users.read` (managing_partner | firm_admin). */
    list: () => request<FirmUser[]>('/users'),
    /** Active, non-client staff for the case-creation / member pickers (cases.assign). */
    assignable: () => request<AssignableUser[]>('/users/assignable'),
    /**
     * `POST /users/{userId}/totp-reset` — managing_partner ONLY (server enforces
     * `users.deactivate` + the MP-only internal gate). Clears the target's TOTP so
     * their next login re-enrols.
     */
    resetTotp: (userId: string) =>
      request<OkResponse>(`/users/${encodeURIComponent(userId)}/totp-reset`, {
        method: 'POST',
        json: {},
      }),
  },
  firms: {
    getSettings: () => request<FirmSettings>('/firms/settings'),
    updateSettings: (body: FirmSettings) =>
      request<FirmSettings>('/firms/settings', { method: 'PUT', json: body }),
    uploadLogo: (file: Blob, contentType: string) =>
      request<LogoUploadResponse>('/firms/logo', {
        method: 'POST',
        body: file,
        headers: { 'content-type': contentType },
      }),
  },
  /** The "My work" landing (`GET /dashboard`): active cases, counts, firm activity, alerts. */
  dashboard: {
    get: () => request<DashboardResponse>('/dashboard'),
  },

  /**
   * Client surface (PO finding 2026-10-05 — "a client entity"). `{clientId}` is the ULID
   * `client_id`. Gated like the case surface: `list`/`get` on `cases.list`, `create` on
   * `cases.create`, `update` on `cases.write`.
   */
  clients: {
    list: (params?: ClientListParams) => request<ClientListResponse>(`/clients${toQuery(params)}`),
    create: (body: CreateClientRequest) =>
      request<ClientSummary>('/clients', { method: 'POST', json: body }),
    get: (clientId: string) => request<ClientDetail>(`/clients/${encodeURIComponent(clientId)}`),
    update: (clientId: string, body: UpdateClientRequest) =>
      request<ClientSummary>(`/clients/${encodeURIComponent(clientId)}`, {
        method: 'PATCH',
        json: body,
      }),
  },

  /**
   * Case surface. The `{caseId}` path segment carries the HUMAN `case_number`
   * (`ARG-YYYY-NNNN`, the OpenAPI `CaseId`), NOT the internal ULID `id` — the SPA routes
   * on `case_number` and the backend resolves it to the ULID `case_id` server-side (the
   * `id` field on the `Case` body remains the ULID for embeds/joins). So every `id`-taking
   * method below expects the `case_number` string.
   */
  cases: {
    list: (params?: CaseListParams) => request<CaseListResponse>(`/cases${toQuery(params)}`),
    create: (body: CreateCaseRequest) => request<Case>('/cases', { method: 'POST', json: body }),
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    get: (caseNumber: string) => request<Case>(`/cases/${encodeURIComponent(caseNumber)}`),
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). Returns the updated Case. */
    update: (caseNumber: string, body: UpdateCaseRequest) =>
      request<Case>(`/cases/${encodeURIComponent(caseNumber)}`, { method: 'PATCH', json: body }),
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    dashboard: (caseNumber: string) =>
      request<CaseDashboard>(`/cases/${encodeURIComponent(caseNumber)}/dashboard`),
    members: {
      /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
      list: (caseNumber: string) =>
        request<CaseMembersResponse>(`/cases/${encodeURIComponent(caseNumber)}/members`),
      /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
      add: (caseNumber: string, body: AddCaseMemberRequest) =>
        request<void>(`/cases/${encodeURIComponent(caseNumber)}/members`, {
          method: 'POST',
          json: body,
        }),
      /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
      update: (caseNumber: string, userId: string, body: { role: CaseRole }) =>
        request<void>(
          `/cases/${encodeURIComponent(caseNumber)}/members/${encodeURIComponent(userId)}`,
          { method: 'PATCH', json: body },
        ),
      /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
      remove: (caseNumber: string, userId: string) =>
        request<void>(
          `/cases/${encodeURIComponent(caseNumber)}/members/${encodeURIComponent(userId)}`,
          { method: 'DELETE' },
        ),
    },
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    close: (caseNumber: string, body?: { resolution_note?: string }) =>
      request<void>(`/cases/${encodeURIComponent(caseNumber)}/close`, {
        method: 'POST',
        json: body ?? {},
      }),
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    archive: (caseNumber: string) =>
      request<void>(`/cases/${encodeURIComponent(caseNumber)}/archive`, { method: 'POST' }),
  },
  /**
   * Documents surface (Sprint 3). Every case-scoped path addresses the case by
   * its human `case_number` (like `api.cases.*`); documents by their ULID id.
   * The 3-step upload flow: `initiate` → `putToUploadUrl` (the raw token-authed
   * PUT, exported above) → `finalize`.
   */
  documents: {
    list: (caseNumber: string, params?: DocumentListParams) =>
      request<DocumentListResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/documents${toQuery(params)}`,
      ),
    initiate: (
      caseNumber: string,
      body: { filename: string; mime_type: string; size_bytes: number },
    ) =>
      request<InitiateUploadResponse>(`/cases/${encodeURIComponent(caseNumber)}/documents`, {
        method: 'POST',
        json: body,
      }),
    putToUploadUrl,
    finalize: (caseNumber: string, docId: string) =>
      request<CaseDocument>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/finalize`,
        { method: 'POST', json: {} },
      ),
    get: (caseNumber: string, docId: string) =>
      request<CaseDocument>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}`,
      ),
    /** The 302-redirecting download endpoint. Used as a media `src` (iframe/img):
     *  the browser sends the session cookie, follows the 302, and renders the
     *  tokened URL — the SPA never fetches the bytes through the JSON wrapper.
     *  `disposition` distinguishes an in-app inline VIEW (preview-open → audited
     *  `document_viewed`) from an explicit EXPORT (`attachment`, the default →
     *  `document_downloaded`) — forensic chain-of-custody fidelity (Batch-D). */
    downloadUrl: (
      caseNumber: string,
      docId: string,
      disposition: 'inline' | 'attachment' = 'attachment',
    ) =>
      `${API_BASE}/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/download?disposition=${disposition}`,
    transcript: (caseNumber: string, docId: string) =>
      request<Transcript>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/transcript`,
      ),
    extractedFields: (caseNumber: string, docId: string) =>
      request<Record<string, unknown>>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/extracted-fields`,
      ),
    patchFields: (caseNumber: string, docId: string, fields: Record<string, unknown>) =>
      request<Record<string, unknown>>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/extracted-fields`,
        { method: 'PATCH', json: { fields } },
      ),
    reocr: (caseNumber: string, docId: string, engine: Exclude<OcrEngine, 'manual'>) =>
      request<void>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/reocr`,
        { method: 'POST', json: { engine } },
      ),
    /**
     * `POST …/documents/{docId}/reextract` (PO finding 2026-10-05 #6) — re-run extraction
     * off the existing OCR transcript, rebuilding the document's transactions. 202 on
     * success; 409 `conflict` when OCR is not done (surfaced via the typed `ApiError`).
     */
    reextract: (caseNumber: string, docId: string) =>
      request<ReextractResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/reextract`,
        { method: 'POST', json: {} },
      ),
    manualReview: (caseNumber: string, docId: string, body: { text: string; note?: string }) =>
      request<CaseDocument>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/manual-review`,
        { method: 'POST', json: body },
      ),
    legalHold: (
      caseNumber: string,
      docId: string,
      body: { legal_hold: boolean; reason?: string },
    ) =>
      request<void>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/legal-hold`,
        { method: 'PUT', json: body },
      ),
    selfReview: (caseNumber: string, docId: string, body: { confirmed: true; note?: string }) =>
      request<CaseDocument>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}/self-review`,
        { method: 'POST', json: body },
      ),
    remove: (caseNumber: string, docId: string, reason?: string) =>
      request<void>(
        `/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(docId)}`,
        { method: 'DELETE', ...(reason ? { json: { reason } } : {}) },
      ),
  },
  /** OS share-target "Pick a case" surface (T-3.13 / US-026). */
  shareTarget: {
    /** Display metadata for inbox receipts; unknown/foreign ids are omitted. */
    inbox: (ids: string[]) =>
      request<InboxMetaResponse>(`/share-target/inbox${toQuery({ ids: ids.join(',') })}`),
    attach: (inboxId: string, caseNumber: string) =>
      request<CaseDocument>(`/share-target/${encodeURIComponent(inboxId)}/attach`, {
        method: 'POST',
        json: { case_number: caseNumber },
      }),
  },
  /**
   * Statement-analysis + transactions surface (Sprint 4 / T-4.09/T-4.16; US-030/031/032).
   * Every case-scoped path addresses the case by its human `case_number` (like
   * `api.cases.*`); the backend resolves it to the ULID case_id before authz. Money in
   * every response is signed int64 kobo (CC-6).
   */
  statement: {
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    transactions: (caseNumber: string, params?: TransactionListParams) =>
      request<TransactionListResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/transactions${toQuery(params)}`,
      ),
    transaction: (caseNumber: string, txnId: string) =>
      request<Transaction>(
        `/cases/${encodeURIComponent(caseNumber)}/transactions/${encodeURIComponent(txnId)}`,
      ),
    transactionFlags: (caseNumber: string, txnId: string) =>
      request<{ flags: RiskFlag[] }>(
        `/cases/${encodeURIComponent(caseNumber)}/transactions/${encodeURIComponent(txnId)}/flags`,
      ),
    summary: (caseNumber: string) =>
      request<TransactionSummary>(`/cases/${encodeURIComponent(caseNumber)}/transactions/summary`),
    /** The 302-safe CSV export URL — used as an anchor href (browser sends the cookie). */
    exportUrl: (caseNumber: string) =>
      `${API_BASE}/cases/${encodeURIComponent(caseNumber)}/transactions/export?format=csv`,
    excessCharges: (caseNumber: string) =>
      request<ExcessChargesResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/excess-charges`,
      ),
    cashlessPolicy: (caseNumber: string) =>
      request<CashlessPolicyResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/cashless-policy`,
      ),
    largeTransactions: (caseNumber: string, topN?: number) =>
      request<LargeTransactionsResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/large-transactions${toQuery({
          top_n: topN,
        })}`,
      ),
    anomalies: (caseNumber: string) =>
      request<AnomaliesResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/anomalies`,
      ),
    reconciliation: (caseNumber: string) =>
      request<ReconciliationResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/reconciliation`,
      ),
    /** Re-run the full auto-raise pipeline against current data (contributor+; audited). */
    run: (caseNumber: string) =>
      request<RunAnalysisResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/statement-analysis/run`,
        { method: 'POST', json: {} },
      ),
  },
  /**
   * Counterparties surface (Sprint 4 / T-4.15; US-130). Case-scoped like `api.statement.*`
   * (the `{caseId}` segment is the human case_number). Every money value is int64 kobo (CC-6);
   * raw tin/bvn/bank_account are never returned (CC-4).
   */
  counterparties: {
    /** @param caseNumber the human `ARG-YYYY-NNNN` case_number (path id). */
    list: (caseNumber: string, params?: CounterpartyListParams) =>
      request<CounterpartyListResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/counterparties${toQuery(params)}`,
      ),
    get: (caseNumber: string, counterpartyId: string) =>
      request<Counterparty>(
        `/cases/${encodeURIComponent(caseNumber)}/counterparties/${encodeURIComponent(counterpartyId)}`,
      ),
    patch: (caseNumber: string, counterpartyId: string, body: CounterpartyPatch) =>
      request<Counterparty>(
        `/cases/${encodeURIComponent(caseNumber)}/counterparties/${encodeURIComponent(counterpartyId)}`,
        { method: 'PATCH', json: body },
      ),
    transactions: (caseNumber: string, counterpartyId: string) =>
      request<{ transactions: Transaction[] }>(
        `/cases/${encodeURIComponent(caseNumber)}/counterparties/${encodeURIComponent(counterpartyId)}/transactions`,
      ),
  },
  /**
   * Sanctions surface (Sprint 4 / T-4.14; US-130). TENANT-scoped (not case-scoped): the
   * ad-hoc screen (audited compliance action), the per-counterparty cached hits, and the
   * list-cache metadata. The screen serves a STALE snapshot on an OpenSanctions outage.
   */
  sanctions: {
    screen: (body: SanctionsScreenRequest) =>
      request<SanctionsScreenResponse>('/sanctions/screen', { method: 'POST', json: body }),
    hits: (counterpartyId: string) =>
      request<SanctionsHitsResponse>(`/sanctions/hits/${encodeURIComponent(counterpartyId)}`),
    lists: () =>
      request<{ lists: Array<{ list: SanctionsFlag; entry_count: number; refreshed_at: string }> }>(
        '/sanctions/lists',
      ),
  },
  /**
   * Audit-trail surface (Sprint 6 / T-6.14; US-080). Case paths address the case
   * by its human `case_number` (like `api.cases.*`). Reads emit no audit events;
   * the two exports are `audit.export`-gated (managing partner) and audited
   * server-side. Export URLs are used as anchor hrefs — the browser sends the
   * session cookie and downloads the attachment directly.
   */
  audit: {
    list: (caseNumber: string, params?: AuditListParams) =>
      request<AuditListResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/audit${toQuery(params)}`,
      ),
    verify: (caseNumber: string) =>
      request<AuditVerifyResponse>(`/cases/${encodeURIComponent(caseNumber)}/audit/verify`, {
        method: 'POST',
        json: {},
      }),
    caseExportUrl: (caseNumber: string) =>
      `${API_BASE}/cases/${encodeURIComponent(caseNumber)}/audit/export`,
    firmExportUrl: () => `${API_BASE}/audit/export`,
    integrity: () => request<AuditIntegrityResponse>('/audit/integrity'),
  },
  /**
   * Reference-data surface (Sprint 6 / T-6.05/T-6.06; US-120/121/122). CONTROL-PLANE,
   * cross-tenant-identical data read through the tenant-bound API (requireAuth only —
   * every firm user reads the regulatory reference library). Every lookup takes an
   * optional `date` ('in force on this date'; absent = today). Reads emit no audit
   * events.
   */
  reference: {
    cbnGuides: (date?: string) =>
      request<CbnGuidesResponse>(`/reference/cbn-guides${toQuery({ date })}`),
    mpr: (date?: string) => request<MprResponse>(`/reference/mpr${toQuery({ date })}`),
    whtRates: (params?: { date?: string; category?: string }) =>
      request<WhtRatesResponse>(`/reference/wht-rates${toQuery(params)}`),
    pitBands: (date?: string) => request<PitBand[]>(`/reference/pit-bands${toQuery({ date })}`),
    citRates: (appliesTo: CompanyClass, date?: string) =>
      request<CitRateResponse>(`/reference/cit-rates${toQuery({ applies_to: appliesTo, date })}`),
    stampDuty: (params?: { date?: string; instrument_type?: string }) =>
      request<StampDutyResponse>(`/reference/stamp-duty${toQuery(params)}`),
    devLevy: (params?: { date?: string; applies_to?: CompanyClass }) =>
      request<DevLevyResponse>(`/reference/dev-levy${toQuery(params)}`),
    statute: (citation: string) =>
      request<Statute>(`/reference/statutes/${encodeURIComponent(citation)}`),
    /** The statute-directory list (`?category=` filter; empty list = honest browse answer). */
    statutes: (params?: { category?: string }) =>
      request<StatuteListResponse>(`/reference/statutes${toQuery(params)}`),
  },
  /**
   * Reports surface (Sprint 6 / T-6.10; US-070). Case paths address the case by its
   * human `case_number` (like `api.cases.*`). Money in report bodies is int64 kobo
   * (CC-6). `report_downloaded` / `report_superseded` are audited server-side.
   *
   * ⚠️ Only `courtBundle` + `sign` (Batch C) have a live backend today; the CRUD
   * methods below (list/generate/get/remove/preview/regenerate/verify + downloadUrl)
   * consume routes that are NOT yet implemented — see the Reports doc block above.
   */
  reports: {
    list: (caseNumber: string) =>
      request<ReportListResponse>(`/cases/${encodeURIComponent(caseNumber)}/reports`),
    generate: (caseNumber: string, body: GenerateReportRequest) =>
      request<Report>(`/cases/${encodeURIComponent(caseNumber)}/reports`, {
        method: 'POST',
        json: body,
      }),
    get: (caseNumber: string, reportId: string) =>
      request<Report>(
        `/cases/${encodeURIComponent(caseNumber)}/reports/${encodeURIComponent(reportId)}`,
      ),
    remove: (caseNumber: string, reportId: string) =>
      request<void>(
        `/cases/${encodeURIComponent(caseNumber)}/reports/${encodeURIComponent(reportId)}`,
        { method: 'DELETE' },
      ),
    /** In-page HTML (no R2 write) for the "Preview" affordance. */
    preview: (caseNumber: string, body: { report_type: ReportType }) =>
      request<ReportPreviewResponse>(`/cases/${encodeURIComponent(caseNumber)}/reports/preview`, {
        method: 'POST',
        json: body,
      }),
    /** Regenerate → a NEW version; supersedes the prior row (`report_superseded`). */
    regenerate: (caseNumber: string, reportId: string) =>
      request<Report>(
        `/cases/${encodeURIComponent(caseNumber)}/reports/${encodeURIComponent(reportId)}/regenerate`,
        { method: 'POST', json: {} },
      ),
    verify: (caseNumber: string, reportId: string) =>
      request<ReportVerifyResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/reports/${encodeURIComponent(reportId)}/verify`,
        { method: 'POST', json: {} },
      ),
    /** The 302-redirecting per-format download URL (used as an anchor href / iframe src —
     *  the browser sends the session cookie, follows the 302 to the signed R2 URL). An
     *  inline `disposition` renders the HTML in the preview iframe; `attachment` (default)
     *  is the explicit export (`report_downloaded`). Mirrors `api.documents.downloadUrl`. */
    downloadUrl: (
      caseNumber: string,
      reportId: string,
      format: ReportFormat,
      disposition: 'inline' | 'attachment' = 'attachment',
    ) =>
      `${API_BASE}/cases/${encodeURIComponent(caseNumber)}/reports/${encodeURIComponent(reportId)}/download?format=${format}&disposition=${disposition}`,
  },

  /**
   * AI-assisted features (Sprint 7 / T-7.12; US-110). `summariseReport` POSTs a request
   * for an executive-summary SUGGESTION of a completed report (202 → a queued job); the
   * client polls `getSummaryJob` until `status === 'succeeded'`, then shows `summary` as a
   * SUGGESTION the user accepts/edits — DD-010: it is NEVER auto-inserted into the signed
   * report body. A firm without `ai_features_enabled` (403) or at its monthly AI cost cap
   * (402) is surfaced as the standard typed ApiError.
   */
  ai: {
    summariseReport: (reportId: string, maxWords?: number) =>
      request<AiSummaryJob>(`/ai/summarise-report`, {
        method: 'POST',
        json:
          maxWords === undefined
            ? { report_id: reportId }
            : { report_id: reportId, max_words: maxWords },
      }),
    getSummaryJob: (jobId: string) =>
      request<AiSummaryJob>(`/ai/summarise-report/${encodeURIComponent(jobId)}`),
  },

  /**
   * Annotations (Sprint 7 / T-7.09; US-091) — the durable document/transaction-anchored
   * comment threads. list/create/edit/remove + resolve/reopen; a threaded reply passes
   * `parent_annotation_id`. Each durable commit is notified live over the collab channel
   * (see `subscribeCaseUpdates`), but the D1 row served here is the source of truth.
   */
  annotations: {
    list: (caseNumber: string, params?: { document_id?: string; include_resolved?: boolean }) =>
      request<AnnotationListResponse>(
        `/cases/${encodeURIComponent(caseNumber)}/annotations${toQuery(params)}`,
      ),
    create: (caseNumber: string, body: CreateAnnotationInput) =>
      request<Annotation>(`/cases/${encodeURIComponent(caseNumber)}/annotations`, {
        method: 'POST',
        json: body,
      }),
    edit: (caseNumber: string, annotationId: string, body: string) =>
      request<Annotation>(
        `/cases/${encodeURIComponent(caseNumber)}/annotations/${encodeURIComponent(annotationId)}`,
        { method: 'PATCH', json: { body } },
      ),
    remove: (caseNumber: string, annotationId: string) =>
      request<void>(
        `/cases/${encodeURIComponent(caseNumber)}/annotations/${encodeURIComponent(annotationId)}`,
        { method: 'DELETE' },
      ),
    resolve: (caseNumber: string, annotationId: string) =>
      request<Annotation>(
        `/cases/${encodeURIComponent(caseNumber)}/annotations/${encodeURIComponent(annotationId)}/resolve`,
        { method: 'POST', json: {} },
      ),
    reopen: (caseNumber: string, annotationId: string) =>
      request<Annotation>(
        `/cases/${encodeURIComponent(caseNumber)}/annotations/${encodeURIComponent(annotationId)}/reopen`,
        { method: 'POST', json: {} },
      ),
  },
  /**
   * DSAR fulfilment surface (Sprint 7 / T-7.14; US-153). Firm-admin, managing_partner
   * ONLY — `dsar.read` for the queue, `dsar.manage` for export/rectify/erase (the server
   * enforces both). The export is a 200 JSON attachment DOWNLOAD, exposed as an anchor
   * href (the browser sends the session cookie, exactly like `audit.firmExportUrl`) — it
   * is NEVER fetched through the JSON wrapper. `erase` returns 200 (erased) | 202
   * (deferred → status `in_progress`) — both are 2xx so `request` resolves; a 409 refusal
   * (legal hold / retention floor) throws the typed ApiError carrying `details.reason`.
   */
  dsar: {
    list: (status?: string) => request<DsarListResponse>(`/dsar/requests${toQuery({ status })}`),
    /** The 200-JSON-attachment export URL — used as an anchor href (browser sends the cookie). */
    exportUrl: (requestId: string) =>
      `${API_BASE}/dsar/requests/${encodeURIComponent(requestId)}/export`,
    rectify: (requestId: string, field: DsarRectifyField, value: string) =>
      request<DsarRequest>(`/dsar/requests/${encodeURIComponent(requestId)}/rectify`, {
        method: 'POST',
        json: { field, value },
      }),
    /** 200 erased | 202 deferred (status `in_progress`) — both resolve; 409 → ApiError (details.reason). */
    erase: (requestId: string) =>
      request<DsarRequest>(`/dsar/requests/${encodeURIComponent(requestId)}/erase`, {
        method: 'POST',
      }),
  },
} as const;

export type { Permission, Role };
